export interface Vec2 {
  x: number;
  y: number;
}

/**
 * A 2D affine transform, stored as the six meaningful numbers of a 3x3.
 *
 * ```
 * | a c tx |
 * | b d ty |
 * | 0 0  1 |
 * ```
 *
 * The same layout the 2D canvas API and every 2D engine uses, so a matrix can
 * be handed straight to one without shuffling.
 */
export interface Mat2D {
  a: number;
  b: number;
  c: number;
  d: number;
  tx: number;
  ty: number;
}

export const vec2 = (x = 0, y = 0): Vec2 => ({ x, y });

export const addVec = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x + b.x, y: a.y + b.y });
export const subVec = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x - b.x, y: a.y - b.y });
export const scaleVec = (v: Vec2, k: number): Vec2 => ({ x: v.x * k, y: v.y * k });
export const dot = (a: Vec2, b: Vec2): number => a.x * b.x + a.y * b.y;
/** The z of the 3D cross product; positive when `b` is anticlockwise of `a`. */
export const cross = (a: Vec2, b: Vec2): number => a.x * b.y - a.y * b.x;
export const lengthOf = (v: Vec2): number => Math.hypot(v.x, v.y);
export const distance = (a: Vec2, b: Vec2): number => Math.hypot(b.x - a.x, b.y - a.y);

export function normalize(v: Vec2): Vec2 {
  const len = Math.hypot(v.x, v.y);
  return len > 0 ? { x: v.x / len, y: v.y / len } : { x: 0, y: 0 };
}

export const lerpVec = (a: Vec2, b: Vec2, t: number): Vec2 => ({
  x: a.x + (b.x - a.x) * t,
  y: a.y + (b.y - a.y) * t,
});

/** Rotate a vector by `radians`, anticlockwise in a y-down space. */
export function rotateVec(v: Vec2, radians: number): Vec2 {
  const s = Math.sin(radians);
  const c = Math.cos(radians);
  return { x: v.x * c - v.y * s, y: v.x * s + v.y * c };
}

export const identity = (): Mat2D => ({ a: 1, b: 0, c: 0, d: 1, tx: 0, ty: 0 });

/** `first` then `second` — the transform you get by applying them in order. */
export function multiply(first: Mat2D, second: Mat2D): Mat2D {
  return {
    a: first.a * second.a + first.b * second.c,
    b: first.a * second.b + first.b * second.d,
    c: first.c * second.a + first.d * second.c,
    d: first.c * second.b + first.d * second.d,
    tx: first.tx * second.a + first.ty * second.c + second.tx,
    ty: first.tx * second.b + first.ty * second.d + second.ty,
  };
}

/** Translate, then rotate, then scale — the order a scene node composes in. */
export function compose(position: Vec2, rotation: number, scale: Vec2): Mat2D {
  const s = Math.sin(rotation);
  const c = Math.cos(rotation);
  return {
    a: c * scale.x,
    b: s * scale.x,
    c: -s * scale.y,
    d: c * scale.y,
    tx: position.x,
    ty: position.y,
  };
}

export function transformPoint(m: Mat2D, p: Vec2): Vec2 {
  return { x: m.a * p.x + m.c * p.y + m.tx, y: m.b * p.x + m.d * p.y + m.ty };
}

/** As `transformPoint`, but ignoring translation — for directions. */
export function transformVector(m: Mat2D, v: Vec2): Vec2 {
  return { x: m.a * v.x + m.c * v.y, y: m.b * v.x + m.d * v.y };
}

/**
 * The inverse, or the identity for a singular matrix.
 *
 * Returning the identity rather than throwing is deliberate: a zero scale is
 * an ordinary thing for a game to do — an object shrinking out of existence —
 * and a screen-to-world call is not a place to crash over it.
 */
export function invert(m: Mat2D): Mat2D {
  const det = m.a * m.d - m.b * m.c;
  if (det === 0 || !Number.isFinite(det)) return identity();
  const inv = 1 / det;
  return {
    a: m.d * inv,
    b: -m.b * inv,
    c: -m.c * inv,
    d: m.a * inv,
    tx: (m.c * m.ty - m.d * m.tx) * inv,
    ty: (m.b * m.tx - m.a * m.ty) * inv,
  };
}

/** An axis-aligned box. Used for bounds, culling and collision alike. */
export interface Rect2D {
  x: number;
  y: number;
  width: number;
  height: number;
}

export const rectOf = (x: number, y: number, width: number, height: number): Rect2D => ({
  x,
  y,
  width,
  height,
});

export function rectsOverlap(a: Rect2D, b: Rect2D): boolean {
  return (
    a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y
  );
}

export function rectContainsPoint(r: Rect2D, p: Vec2): boolean {
  return p.x >= r.x && p.x < r.x + r.width && p.y >= r.y && p.y < r.y + r.height;
}

/** The box enclosing both. */
export function rectUnion(a: Rect2D, b: Rect2D): Rect2D {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  return {
    x,
    y,
    width: Math.max(a.x + a.width, b.x + b.width) - x,
    height: Math.max(a.y + a.height, b.y + b.height) - y,
  };
}

/** The box enclosing a rect once `m` has been applied to its four corners. */
export function transformRect(m: Mat2D, r: Rect2D): Rect2D {
  const corners = [
    transformPoint(m, { x: r.x, y: r.y }),
    transformPoint(m, { x: r.x + r.width, y: r.y }),
    transformPoint(m, { x: r.x, y: r.y + r.height }),
    transformPoint(m, { x: r.x + r.width, y: r.y + r.height }),
  ];
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const c of corners) {
    minX = Math.min(minX, c.x);
    minY = Math.min(minY, c.y);
    maxX = Math.max(maxX, c.x);
    maxY = Math.max(maxY, c.y);
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

export const clamp = (v: number, lo: number, hi: number): number =>
  v < lo ? lo : v > hi ? hi : v;

export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
